import { useEffect, useMemo, useState } from "react"
import { Data2, newId } from "../../../data-types"
import { Form, FormField, ModalId, FieldType, option, O, FieldValue, useUIState, Validatable, BooleanField, TextField, useEffectAfterMount } from "../../../ui"
import { Localidad, Provincia } from "../../../../services/gob-ar"
import { addressHelpers } from "../../hooks/addressHooks"
import { capitalize } from "../../../../utils/typeUtils"
import { GoogleMap } from '../google-map/googleMap'
import { toList } from '../../../../utils/listUtils'
import styles from './addressForm.module.css'

type Props = {
    addressData?: Data2
    provinces: Provincia[]
    localities: Localidad[]
    defaultProvince: O<Provincia>
    defaultLocality: O<Localidad>
    onSubmit: (data: Data2) => void
    onCancel?: () => void
}

export const AddressForm = (props: Props) => {
    const { addressData, provinces, localities, defaultProvince, defaultLocality, onSubmit, onCancel } = props
    const createMode = !addressData

    const getProvinceLocalities = (provinceId?: string) => localities.filter(locality => locality.provincia_id === provinceId)

    const [address, setAddress] = useState<O<string>>(addressData?.address as O<string>)
    const defaultProvinceLocalities = useMemo(() => getProvinceLocalities(defaultProvince?.id), [])
    const [provinceLocalities, setProvinceLocalities] = useState<Localidad[]>(defaultProvinceLocalities)
    const [showBetweenStreets, setShowBetweenStreets] = useState<boolean>(!!addressData?.betweenStreets)
    const [betweenStreets, setBetweenStreets] = useState<O<string>>(addressData?.betweenStreets as O<string>)
    const [province, setProvince] = useState<O<Provincia>>(defaultProvince)
    const [locality, setLocality] = useState<O<Localidad>>(defaultLocality)
    const [focusAddress, setFocusAddress] = useState(false)
    const [errorMode, setErrorMode] = useState(false)
    const [mapAddress, setMapAddress] = useState<O<string>>()

    const { openedModal } = useUIState()

    const { describeAddress } = addressHelpers

    useEffect(() => {
        provinces.length === 0 && localities.length === 0 && setErrorMode(true)
    }, [])

    useEffectAfterMount(() => {
        setLocality(undefined)
        setProvinceLocalities(getProvinceLocalities(province?.id))
    }, [province])

    useEffect(() => {
        setTimeout(() => setFocusAddress(openedModal === ModalId.ADDRESS_FORM), 500)
    }, [openedModal])
    
    useEffect(() => {
        if (address && !errorMode) {
            const handler = setTimeout(() => {
                setMapAddress(describeAddress({ address, locality: locality?.nombre, province: province?.nombre }))
            }, 1000)
            return () => clearTimeout(handler)
        }
        setMapAddress(undefined)
    }, [address, errorMode])

    useEffect(() => {
        if (address && !errorMode) {
            setMapAddress(describeAddress({ address, locality: locality?.nombre, province: province?.nombre }))
        }
    }, [province, locality, errorMode])

    const onChangeAddress = (value?: FieldValue) => setAddress(value as O<string>)

    const onChangeShowBetweenStreets = (value?: FieldValue) => {
        setShowBetweenStreets(value as boolean)
        !value && setBetweenStreets(undefined)
    }

    const onChangeProvince = (value?: FieldValue) => {
        const newProvince = provinces.find(province => province.id === value)
        setProvince(newProvince)
    }

    const onChangeLocality = (value?: FieldValue) => {
        const newLocality = provinceLocalities.find(locality => locality.id === value)
        setLocality(newLocality)
    }

    const fields: FormField[] = [
        {
            name: 'address',
            type: FieldType.TEXT,
            defaultValue: address,
            label: 'Dirección',
            focus: focusAddress,
            onChange: onChangeAddress
        },
        {
            name: 'betweenStreets',
            type: FieldType.TEXT,
            label: 'Entre calles',
            render: () => (
                <div className={styles.betweenStreets}>
                    <BooleanField
                        id="showBetweenStreets"
                        value={showBetweenStreets}
                        optional
                        onChange={onChangeShowBetweenStreets}
                    />
                    {showBetweenStreets && (
                        <>
                            <span>Entre</span>
                            <TextField
                                id="betweenStreets"
                                value={betweenStreets}
                                freeValue
                                optional
                                onChange={setBetweenStreets}
                            />
                        </>
                    )}
                </div>
            )
        },
        {
            name: 'provinceId',
            type: FieldType.SELECT,
            value: province?.id,
            label: 'Provincia',
            options: provinces.map(province => option(province.id, province.nombre)),
            disabled: provinces.length === 0,
            optional: true,
            onChange: onChangeProvince
        },
        {
            name: 'localityId',
            type: FieldType.SELECT,
            value: locality?.id,
            label: 'Localidad',
            options: provinceLocalities.map(locality => option(locality.id, locality.nombre)),
            disabled: !province,
            optional: true,
            onChange: onChangeLocality
        },
        {
            name: 'map',
            type: FieldType.IMAGE,
            render: () => (
                <GoogleMap addressesData={toList({ address: mapAddress! })} />
            )
        }
    ]
    if (errorMode) {
        const errorField = {
            name: 'error',
            type: FieldType.TEXT,
            render: () => <Validatable
                validations={toList("Error de conexión. Editar dirección en otro momento.")}
                expanded
            />
        }
        fields.splice(0, 0, errorField)
    }

    const handleSubmit = (data: Data2) => {
        const newData: Data2 = {
            id: addressData?.id || newId(),
            address: capitalize(data.address! as string),
            betweenStreets: showBetweenStreets ? betweenStreets : undefined,
            province: province?.nombre,
            locality: locality?.nombre
        }
        onSubmit(newData)
    }

    const handleCancel = () => onCancel && onCancel()

    return (
        <Form
            formId="address-form"
            fields={fields}
            createMode={createMode}
            createLabel="Agregar"
            onSubmit={handleSubmit}
            onCancel={handleCancel}
        />
    )
}
